import { ComponentType, Fragment, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { IconProps } from '@components/icons';
import { IconApplicants } from '@components/icons/applicants';
import { IconEmail } from '@components/icons/email';
import { IconJob } from '@components/icons/job';
import { IconNotifications } from '@components/icons/notifications';
import { IconOnboarding } from '@components/icons/onboarding';
import { IntegrationCard } from '@components/integration-card';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { theme } from '@styles/theme';

import { api } from 'api';

import { Box } from 'components/Box';
import { Header } from 'components/headers';
import { PageContent } from 'components/Page';
import { useStore } from 'models/Provider';
import { NotificationType } from 'types/notifications';

interface NotificationSettingItem {
  notificationType: NotificationType;
  text: string;
  hasEmail: boolean;
  hasApp: boolean;
}

interface NotificationSettingSection {
  text: string;
  icon: ComponentType<IconProps>;
  items: Array<NotificationSettingItem>;
}

const notificationTypeAvailability = (t: (arg0: string) => string) => {
  const sections: Array<NotificationSettingSection> = [
    {
      text: t('user.profile.settings.notifications.table.applicant.header'),
      icon: IconApplicants,
      items: [
        {
          notificationType: 'APPLY_JOB',
          text: t('user.profile.settings.notifications.table.applicant.applies'),
          hasEmail: true,
          hasApp: true,
        },
        {
          notificationType: 'MOVE_STAGE',
          text: t('user.profile.settings.notifications.table.applicant.movestage'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'ADD_TO_HIRING_TEAM',
          text: t('user.profile.settings.notifications.table.applicant.addtohiringteam'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'ADD_TO_APPLICATION',
          text: t('user.profile.settings.notifications.table.applicant.addtoapplication'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'BULK_REJECT_APPLICANTS',
          text: t('user.profile.settings.notifications.table.applicant.bulkrejectapplicants'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'ADD_RATING',
          text: t('user.profile.settings.notifications.table.applicant.addrating'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'ADD_NOTE',
          text: t('user.profile.settings.notifications.table.applicant.addnote'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'ADD_NOTE_MENTION',
          text: t('user.profile.settings.notifications.table.applicant.addnotemention'),
          hasEmail: true,
          hasApp: true,
        },
        {
          notificationType: 'APPLICANT_HIRED',
          text: t('user.profile.settings.notifications.table.applicant.applicanthired'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'APPLICANT_REJECTED',
          text: t('user.profile.settings.notifications.table.applicant.applicantrejected'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'APPLICANT_ASSIGNED',
          text: t('user.profile.settings.notifications.table.applicant.applicantassigned'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'APPLICATION_MESSAGE',
          text: t('user.profile.settings.notifications.table.applicant.applicationmessage'),
          hasEmail: true,
          hasApp: true,
        },
        {
          notificationType: 'APPLICATION_ASSIGNED_MESSAGE',
          text: t('user.profile.settings.notifications.table.applicant.applicationassignedmessage'),
          hasEmail: true,
          hasApp: true,
        },
      ],
    },
    {
      text: t('user.profile.settings.notifications.table.job.header'),
      icon: IconJob,
      items: [
        {
          notificationType: 'PUBLISH_JOB',
          text: t('user.profile.settings.notifications.table.job.publishjob'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'ARCHIVE_JOB',
          text: t('user.profile.settings.notifications.table.job.archivejob'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'EXPIRE_JOB',
          text: t('user.profile.settings.notifications.table.job.expirejob'),
          hasEmail: true,
          hasApp: true,
        },
        {
          notificationType: 'ADD_JOB_NOTE',
          text: t('user.profile.settings.notifications.table.job.addjobnote'),
          hasEmail: false,
          hasApp: true,
        },
        {
          notificationType: 'ADD_JOB_NOTE_MENTION',
          text: t('user.profile.settings.notifications.table.job.addjobnotemention'),
          hasEmail: true,
          hasApp: true,
        },
      ],
    },
    {
      text: t('user.profile.settings.notifications.table.onboarding.header'),
      icon: IconOnboarding,
      items: [
        {
          notificationType: 'INTEGRATION',
          text: t('user.profile.settings.notifications.table.onboarding.integration'),
          hasEmail: false,
          hasApp: true,
        },
      ],
    },
  ];

  return sections;
};

export const NotificationSettings = observer(() => {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const settingsQuery = useQuery({
    queryKey: ['notificationSettings'],
    queryFn: api.notifications.loadSettings,
  });
  const sections = notificationTypeAvailability(t);
  const client = useQueryClient();
  const [reset, setReset] = useState(0);

  function updateInAppNotificationSettings(enabled: boolean, notificationType: NotificationType) {
    const fn = enabled
      ? api.notifications.enableInAppNotificationSetting
      : api.notifications.disableInAppNotificationSetting;

    fn(notificationType)
      .then(() => {
        client.refetchQueries(['notificationSettings']);
        showSuccess(t('user.profile.settings.notifications.save.success'));
      })
      .catch(() => {
        setReset((old) => old + 1);
        showError(t('user.profile.settings.notifications.save.error'));
      });
  }

  function updateMailNotificationSettings(enabled: boolean, notificationType: NotificationType) {
    const fn = enabled
      ? api.notifications.enableMailNotificationSetting
      : api.notifications.disableMailNotificationSetting;

    fn(notificationType)
      .then(() => {
        client.refetchQueries(['notificationSettings']);
        showSuccess(t('user.profile.settings.notifications.save.success'));
      })
      .catch(() => {
        setReset((old) => old + 1);
        showError(t('user.profile.settings.notifications.save.error'));
      });
  }

  return (
    <>
      <Header
        // @ts-ignore
        mobileTitle={t('navigation.profile.notifications')}
        breadcrumbs={<Breadcrumb items={[{ label: t('navigation.profile.notifications') }]} />}
      />
      <PageContent>
        <Container>
          <CardContainer>
            <IntegrationCard
              icon={IconEmail}
              header={t('user.profile.settings.notifications.mail.header')}
              text={t('user.profile.settings.notifications.mail.text')}
            />
            <IntegrationCard
              icon={IconNotifications}
              header={t('user.profile.settings.notifications.inapp.header')}
              text={t('user.profile.settings.notifications.inapp.text')}
            />
          </CardContainer>
          <Box mt="24">
            <Text text={t('user.profile.settings.notifications.activity.header')} font="heading2" />
            <Text
              text={t('user.profile.settings.notifications.activity.subheader')}
              color="textSecondary"
            />
          </Box>
          <NotificationList>
            <NotificationRow>
              <IconBox flex grow alignItems="center">
                <IconNotifications color="elementPurpleInvert" />
                <Text
                  tag="label"
                  font="bold14"
                  text={t('user.profile.settings.notifications.table.header.notify')}
                  caps
                />
              </IconBox>
              <Box flex justifyContent="center" alignItems="center" w="88px">
                <Text
                  tag="label"
                  font="bold14"
                  text={t('user.profile.settings.notifications.table.header.mail')}
                  caps
                />
              </Box>
              <Box flex justifyContent="center" alignItems="center" w="88px">
                <Text
                  tag="label"
                  font="bold14"
                  text={t('user.profile.settings.notifications.table.header.inapp')}
                  caps
                />
              </Box>
            </NotificationRow>
            {settingsQuery.isSuccess && (
              <>
                {sections.map((section) => (
                  <Fragment key={section.text}>
                    <NotificationRow>
                      <IconBox flex grow alignItems="center">
                        <section.icon color="elementPurpleInvert" />
                        <Text tag="label" font="bold14" text={section.text} caps />
                      </IconBox>
                    </NotificationRow>
                    {section.items.map((item) => (
                      <NotificationRow key={item.notificationType}>
                        <Box flex grow alignItems="center">
                          <Text font="regular14" text={item.text} />
                        </Box>
                        <Box flex justifyContent="center" alignItems="center" w="88px">
                          {item.hasEmail && (
                            <Switch
                              onChange={(value) =>
                                updateMailNotificationSettings(value, item.notificationType)
                              }
                              defaultValue={
                                settingsQuery.data[item.notificationType]?.notifyByEmail
                              }
                              key={item.notificationType + 'email' + reset}
                              labelVisible={false}
                              label={item.text}
                            />
                          )}
                        </Box>
                        <Box flex justifyContent="center" alignItems="center" w="88px">
                          {item.hasApp && (
                            <Switch
                              onChange={(value) =>
                                updateInAppNotificationSettings(value, item.notificationType)
                              }
                              defaultValue={settingsQuery.data[item.notificationType]?.notifyInApp}
                              key={item.notificationType + 'app' + reset}
                              labelVisible={false}
                              label={item.text}
                            />
                          )}
                        </Box>
                      </NotificationRow>
                    ))}
                  </Fragment>
                ))}
              </>
            )}
          </NotificationList>
        </Container>
      </PageContent>
    </>
  );
});

const IconBox = styled(Box)({
  svg: {
    marginRight: '0.5rem',
  },
});

const NotificationList = styled.ul({
  padding: 0,
});

const NotificationRow = styled.li({
  listStyleType: 'none',
  padding: '1rem 0.5rem',
  margin: 0,
  borderBottom: '1px solid ' + theme.color.uiTrans10,
  display: 'flex',
  justifyContent: 'space-between',
});

const Container = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  '> *': {
    gridColumnStart: 3,
    gridColumnEnd: 10,
  },
});

const CardContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  columnGap: '1.5rem',
  justifyContent: 'space-betwween',
  width: '100%',
  '> div': {
    gridColumnStart: 'span 4',
  },
});
